import { $logger } from '@/boot/logger'
import { $pegApi } from '@/boot/peg-api'
import { useCustomerReturnProfileStore } from '@/stores/customerReturnProfile.store'
import { useCustomerReturnPlanStore } from '@/stores/customerReturnPlan.store'
import { useCustomerReturnValidation } from '@/composables/useCustomerReturnValidation'
import { useCustomerReturnItemEnrichment } from '@/composables/useCustomerReturnItemEnrichment'
import { useCustomerReturnErrorHandler } from '@/composables/useCustomerReturnErrorHandler'

/**
 * Customer Return Order Store
 *
 * Manages order lookup, verification, and line item selection for the customer return flow.
 * Handles order data fetching, OTP requests, and item selection state.
 *
 * @public API:
 * - State: order, orderNumber, orderEmail, loading
 * - Computed: items, isSingleItemOrder, isSingleItemReturnable, selectedItems, hasSelectedItems
 * - Actions: fetchOrder, getSelectedItems, requestEmailOtp, reset, selectItem, updateItemQuantity, updateOrderItem
 */

/**
 * @typedef {Object} OrderLineItem
 * @property {string} platformOrderLineItemId - Platform order line item ID
 * @property {number} quantityAvailableToReturn - Quantity available for return
 * @property {boolean} isReturnable - Whether item is returnable
 * @property {number} selectedQuantity - Quantity selected for return
 * @property {boolean} selected - Whether item is selected
 * @property {Object} image - Item image object
 * @property {Object} shipping - Shipping information
 * @property {string|null} returnableMessage - Message if item not returnable
 * @property {Object} [returnWindow] - Return eligibility window from API
 * @property {boolean} [returnWindow.refund] - Whether refund is available
 * @property {boolean} [returnWindow.inStoreCredit] - Whether store credit is available
 * @property {string} [eligibilityMessage] - Per-item return eligibility message
 */

/**
 * @typedef {Object} Order
 * @property {string} orderNumber - Order number
 * @property {string} retailer - Retailer ID
 * @property {OrderLineItem[]} lineItems - Line items in the order
 */

// ============================================================================
// PRIVATE HELPER FUNCTIONS
// ============================================================================

/**
 * Validate item index is within bounds
 * @param {number} itemIndex - Index to validate
 * @param {Array} items - Items array
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {boolean} True if valid
 * @throws {Error} If validation fails
 * @private
 */
function _validateItemIndex(itemIndex, items, logger) {
  const { isValidItemIndex } = useCustomerReturnValidation()

  if (!isValidItemIndex(itemIndex, items)) {
    logger.error('Invalid item index', { itemIndex, itemsLength: items.length })
    throw new Error('Invalid item index')
  }
  return true
}

/**
 * Validate quantity is within allowed range
 * @param {number} itemIndex - Index of item
 * @param {number} quantity - Quantity to validate
 * @param {OrderLineItem} item - The item to validate against
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {boolean} True if valid
 * @throws {Error} If validation fails
 * @private
 */
function _validateItemQuantity(itemIndex, quantity, item, logger) {
  if (quantity < 1 || quantity > item.quantityAvailableToReturn) {
    logger.error('Invalid quantity for item', {
      itemIndex,
      quantity,
      maxQuantity: item.quantityAvailableToReturn,
    })
    throw new Error('Invalid quantity')
  }
  return true
}

/**
 * Validate item object structure
 * @param {any} item - Item to validate
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {boolean} True if valid
 * @throws {Error} If validation fails
 * @private
 */
function _validateItemObject(item, logger) {
  if (!item || typeof item !== 'object') {
    logger.error('Invalid item object', { item })
    throw new Error('Item must be a valid object')
  }
  return true
}

// ============================================================================
// STORE DEFINITION
// ============================================================================

export const useCustomerReturnOrderStore = defineStore('customerReturnOrder', () => {
  // stores
  const customerReturnProfileStore = useCustomerReturnProfileStore()
  const { customerReturnProfile } = storeToRefs(customerReturnProfileStore)
  const customerReturnPlanStore = useCustomerReturnPlanStore()

  // composables
  const { enrichLineItems } = useCustomerReturnItemEnrichment()
  const { handleOrderFetchError } = useCustomerReturnErrorHandler()

  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Order data with line items
   * @type {import('vue').Ref<Order|null>}
   * @public
   */
  const order = ref(null)

  /**
   * Whether order was found during lookup
   * @type {import('vue').Ref<boolean|null>}
   * @public
   */
  const orderFound = ref(null)

  /**
   * Email associated with order
   * @type {import('vue').Ref<string|null>}
   * @public
   */
  const orderEmail = ref(useUserStore().user?.email || null)

  /**
   * Gift giver's email address (for gift returns)
   * @type {import('vue').Ref<string|null>}
   * @public
   */
  const giftGiverEmail = ref(null)

  /**
   * Current order number
   * @type {import('vue').Ref<string|null>}
   * @public
   */
  const orderNumber = ref(null)

  /**
   * Loading state for async operations
   * @type {import('vue').Ref<boolean>}
   * @public
   */
  const loading = ref(false)

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * All line items from the order
   * @type {import('vue').ComputedRef<OrderLineItem[]>}
   * @public
   */
  const items = computed(() => order.value?.lineItems || [])

  /**
   * Whether this return is a gift return (based on presence of gift giver email)
   * @type {import('vue').ComputedRef<boolean>}
   * @public
   */
  const isGiftReturn = computed(() => {
    return !!giftGiverEmail.value
  })

  /**
   * Check if order has only one item
   * @type {import('vue').ComputedRef<boolean>}
   * @public
   */
  const isSingleItemOrder = computed(() => items.value?.length === 1)

  /**
   * Check if single item order has returnable item
   * @type {import('vue').ComputedRef<boolean>}
   * @public
   */
  const isSingleItemReturnable = computed(() => {
    if (!isSingleItemOrder.value || !items.value?.length) return false
    const singleItem = items.value[0]
    return singleItem.quantityAvailableToReturn > 0
  })

  /**
   * Get all selected items
   * @type {import('vue').ComputedRef<OrderLineItem[]>}
   * @public
   */
  const selectedItems = computed(() => {
    return items.value.filter(item => item.selected === true && item.selectedQuantity > 0)
  })

  /**
   * Check if any items are selected
   * @type {import('vue').ComputedRef<boolean>}
   * @public
   */
  const hasSelectedItems = computed(() => selectedItems.value.length > 0)

  // ============================================================================
  // ACTIONS (PUBLIC API)
  // ============================================================================

  /**
   * Fetch order data and enrich with return-specific information
   * Each line item's returnWindow is processed individually for eligibility
   * @param {string} retailerId - The retailer ID
   * @param {string} orderNum - The order number
   * @returns {Promise<Object>} Order response object
   * @public
   */
  async function fetchOrder(retailerId, orderNum) {
    const { location } = useSessionStore()

    try {
      loading.value = true
      const lat = location?.latitude
      const long = location?.longitude

      order.value = null
      const response = await $pegApi.Retailers.Orders.getSingle(retailerId, orderNum, {
        latitude: lat,
        longitude: long,
        raw: true,
      })

      if (response.statusCode !== 200) {
        throw response
      }

      // Store plan tier and feature flags for the return flow
      customerReturnPlanStore.plan = response.data.plan || null

      // Enrich line items with return-specific data (including per-item eligibility)
      const enrichedLineItems = enrichLineItems(response.data.order.lineItems)

      order.value = {
        retailer: retailerId,
        orderNumber: orderNum,
        ...response.data.order,
        lineItems: enrichedLineItems,
      }

      customerReturnProfile.value = response.data.userReturnProfile
      orderNumber.value = orderNum

      return response
    } catch (error) {
      handleOrderFetchError(error, { retailerId, orderNumber: orderNum })
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Request a new email OTP by re-fetching order data
   * @param {string} retailerId - The retailer ID
   * @param {string} orderNum - The order number
   * @returns {Promise<Object>} Order response object
   * @public
   */
  async function requestEmailOtp(retailerId, orderNum) {
    return fetchOrder(retailerId, orderNum)
  }

  /**
   * Select or deselect an item for return
   * @param {number} itemIndex - Index in the items array
   * @param {boolean} selected - Whether to select or deselect
   * @throws {Error} If item index is invalid
   * @public
   */
  function selectItem(itemIndex, selected = true) {
    _validateItemIndex(itemIndex, items.value, $logger)

    const item = items.value[itemIndex]
    item.selected = selected

    // Reset quantity if deselecting
    if (!selected) {
      item.selectedQuantity = 1
    }
  }

  /**
   * Set whether order was found during lookup
   * @param {boolean} found - Whether order was found
   * @public
   */
  function setOrderFound(found) {
    orderFound.value = found
  }

  /**
   * Update the quantity being returned for an item
   * @param {number} itemIndex - Index in the items array
   * @param {number} quantity - Quantity to return
   * @throws {Error} If item index or quantity is invalid
   * @public
   */
  function updateItemQuantity(itemIndex, quantity) {
    _validateItemIndex(itemIndex, items.value, $logger)

    const item = items.value[itemIndex]
    _validateItemQuantity(itemIndex, quantity, item, $logger)

    item.selectedQuantity = quantity
  }

  /**
   * Update an entire order item (used for bulk updates)
   * @param {OrderLineItem} item - The updated item
   * @param {number} itemIndex - Index in the items array
   * @throws {Error} If item or index is invalid
   * @public
   */
  function updateOrderItem(item, itemIndex) {
    _validateItemObject(item, $logger)
    _validateItemIndex(itemIndex, items.value, $logger)

    const lineItems = order.value.lineItems
    lineItems[itemIndex] = item
    order.value.lineItems = [...lineItems]
  }

  /**
   * Get items that are selected and ready for processing
   * @returns {OrderLineItem[]} Array of selected items
   * @public
   */
  function getSelectedItems() {
    return selectedItems.value
  }

  /**
   * Reset order state
   * @param {Object} options - Reset options
   * @param {boolean} [options.keepEmail=false] - Keep the order email
   * @public
   */
  function reset({ keepEmail = false } = {}) {
    order.value = null
    orderFound.value = null
    orderNumber.value = ''
    giftGiverEmail.value = ''
    if (!keepEmail) {
      orderEmail.value = useUserStore().user?.email || null
    }
    loading.value = false
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    // state
    order,
    orderFound,
    orderNumber,
    orderEmail,
    giftGiverEmail,
    loading,
    // computed
    items,
    isGiftReturn,
    isSingleItemOrder,
    isSingleItemReturnable,
    selectedItems,
    hasSelectedItems,
    // methods
    fetchOrder,
    getSelectedItems,
    requestEmailOtp,
    reset,
    selectItem,
    setOrderFound,
    updateItemQuantity,
    updateOrderItem,
  }
})
